import { Box, Button, Link, Stack } from '@mui/material';
import { EmployeePermissions, UserStatus } from '@synergy/models';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    PaloColumn,
    PaloFilterChips,
    PaloFilterModal,
    PaloListView,
    PaloNotice,
    PaloRolePills,
    PaloRowAction,
    PaloStatusPill,
} from '@synergy/components-palo';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { UrlFilter } from '@synergy/components';
import { tokens } from '@synergy/theme-palo';

import * as EmployeeActions from '../../../common/actions/contacts-logins-actions';
import { Contact } from '../../../common/models/contact';
import {
    ContactsLoginsDeleteEmployeeModal,
    ContactsLoginsDeleteLoginModal,
} from '../contacts-logins-delete-employee-modal';
import { ContactsLoginsDetailModal } from '../contacts-logins-detail-modal';
import { LoginUser, isLoginUser } from '../models/login-user';
import { PaloLimitCell, PaloUsageCell } from './palo-order-limit-cells';
import { ROUTES } from '../../sidebar-menu/navigation-drawer/routes';
import { UserStatusFilter } from '../../../common/components/filter/employee-filter';
import { getUserStatus } from '../../../common/utils/get-contact-type';
import { getUserStatusTranslationId } from '../../../common/utils/get-user-status-translation-id';
import { localizedPositionsSelector } from '../../../common/selectors/positions-selectors';
import { patchUser, updateMainContact } from '../../../common/actions/contacts-logins-actions';
import { updateAccountLastChecked } from '../../../common/actions/account-actions';
import { usePendingApprovalCount } from '../../order-approvals/use-pending-approval-count';
import { useSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

type Person = Contact | LoginUser;

/** As the legacy filter, without its search field — the list header owns that. */
const PaloContactsLoginsFilter = (): ReactElement => (
    <PaloFilterModal>
        <UserStatusFilter />
    </PaloFilterModal>
);

export const PaloContactsLoginsOverview = (): ReactElement => {
    const dispatch = useThunkDispatch();
    const intl = useIntl();
    const [searchParams, setSearchParams] = useSearchParams();
    const data = useSelector(state => state.contactsLogins.paginatedUsers);
    const bonusContactCount = useSelector(state => state.contactsLogins.bonusContactCount);
    const currency = useSelector(state => state.currency);
    const accountNeedsToBeChecked = useSelector(state => state.account?.accountNeedsToBeChecked);
    const user = useSelector(state => state.user);
    const requestingUser = useSelector(state => state.contactsLogins.requestingUser);
    const orderCeilings = useSelector(state => state.contactsLogins.orderCeilings);
    const positions = useSelector(localizedPositionsSelector);
    const pendingApprovals = usePendingApprovalCount();

    const [showUpsertModal, setShowUpsertModal] = useState(false);
    const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);
    const [isDeleteEmployeeModalOpen, setIsDeleteEmployeeModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<Person>();

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);
    const sortKey = searchParams.get('sort') ?? 'lastName';
    const sortOrder = searchParams.get('order') ?? 'asc';
    /*
     * The main contact stands at the top and the admins after it, but only in
     * the order the list opens in: once the user sorts it themselves, theirs is
     * the only order there is (LST-S3a as amended on 12.10.2026, #2283).
     */
    const defaultSorted = 'lastName' === sortKey && 'asc' === sortOrder;
    const rank = (person: Person): number =>
        person.isMainContact ? 0 : UserStatus.ShopAdmin === getUserStatus(person) ? 1 : 2;
    const people = defaultSorted ? [...data.items].sort((a, b) => rank(a) - rank(b)) : data.items;

    useEffect(() => {
        dispatch(EmployeeActions.fetchOrderCeilings());
    }, [dispatch]);

    useEffect(() => {
        dispatch(EmployeeActions.fetchUsers(searchParams));

        return () => {
            dispatch(EmployeeActions.resetUser());
        };
    }, [dispatch, searchParams]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const openModal = (person?: Person): void => {
        setSelectedUser(person);
        setShowUpsertModal(true);
    };

    const closeModal = (): void => {
        setSelectedUser(undefined);
        setShowUpsertModal(false);
        dispatch(EmployeeActions.resetUserModal());
    };

    const confirmDeleteEmployee = (
        reassignTopicsId?: number,
        reassignMainContactId?: number,
        assignBonusContactId?: number
    ): void => {
        setIsDeleteEmployeeModalOpen(false);
        if (!selectedUser?.id) {
            return;
        }
        dispatch(
            EmployeeActions.deleteEmployee(
                selectedUser.id,
                reassignTopicsId,
                reassignMainContactId,
                assignBonusContactId
            )
        );
    };

    const confirmDeleteUser = (reassignTopicsId?: number): void => {
        setIsDeleteUserModalOpen(false);
        if (!selectedUser?.id || !isLoginUser(selectedUser) || selectedUser.id === user?.contact.id) {
            return;
        }
        dispatch(EmployeeActions.deleteUserLogin(selectedUser, reassignTopicsId));
    };

    const togglePermission = (person: Person): void => {
        dispatch(
            EmployeeActions.updateEmployeePermissions(
                person,
                person.permissions.includes(EmployeePermissions.EmployeeOrders)
                    ? person.permissions.filter(permission => permission !== EmployeePermissions.EmployeeOrders)
                    : [...person.permissions, EmployeePermissions.EmployeeOrders]
            )
        );
    };

    /**
     * An order cannot be larger than the month, so a limit per order at or
     * above the monthly one is cut to it — said beside the field for as long
     * as it holds, not in a toast that is gone before it is read (O-13, R-173).
     */
    const cappedHintOf = (perOrder?: number | null, monthly?: number | null): string | undefined =>
        null !== perOrder && undefined !== perOrder && null !== monthly && undefined !== monthly && perOrder >= monthly
            ? intl.formatMessage(
                  { id: 'app.contacts.limits.cappedToMonthly' },
                  { amount: intl.formatNumber(monthly, { style: 'currency', currency }) }
              )
            : undefined;

    /** A limit changed in the list goes the way the person's form sends it. */
    const saveLimit = (person: LoginUser, limits: Pick<LoginUser, 'monthlyOrderCeiling' | 'perOrderOrderCeiling'>) => {
        void dispatch(
            patchUser({
                id: person.id,
                firstName: person.firstName,
                lastName: person.lastName,
                positionId: person.positionId ?? null,
                email: person.email ?? '',
                phone: person.phone || null,
                formOfAddress: person.formOfAddress,
                enableLogin: true,
                monthlyOrderCeiling: person.monthlyOrderCeiling ?? null,
                perOrderOrderCeiling: person.perOrderOrderCeiling ?? null,
                ...limits,
            })
        ).then(() => dispatch(EmployeeActions.fetchUsers(searchParams)));
    };

    const toggleBonusContact = (person: Person): void => {
        // The last bonus contact cannot simply be dropped; the delete dialog
        // asks who takes over instead.
        if (person.isBonusContact && 1 === bonusContactCount) {
            setSelectedUser(person);

            return;
        }

        dispatch(EmployeeActions.updateBonusContact(person, !person.isBonusContact));
    };

    // What the navigation's filters are called on their chips.
    const filterNames = {
        userStatus: [
            ['USER_STATUS_SHOP_USER', 'app.contacts.limits.filter.withLogin'],
            ['USER_STATUS_NO_USER', 'app.contacts.limits.filter.withoutLogin'],
            ['USER_STATUS_SHOP_ADMIN', 'app.contacts.limits.filter.admins'],
        ].map(([value, id]) => ({ value, label: intl.formatMessage({ id }) })),
        limitStatus: [
            ['NEAR_LIMIT', 'app.contacts.limits.filter.nearLimit'],
            ['APPROVAL_ALWAYS', 'app.contacts.limits.filter.approvalAlways'],
        ].map(([value, id]) => ({ value, label: intl.formatMessage({ id }) })),
    };

    const columns: PaloColumn<Person>[] = [
        {
            key: 'lastName',
            type: 'name',
            head: <FormattedMessage id="app.contacts.limits.name" />,
            sortKey: 'lastName',
            cell: person => (
                <Stack direction="row" gap={1} alignItems="center">
                    <Box
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            color: isLoginUser(person) ? undefined : tokens.color.ink3,
                        }}
                    >
                        {person.lastName} {person.firstName}
                    </Box>
                    <PaloRolePills
                        roles={[
                            person.isMainContact && <FormattedMessage id="app.contacts.logins.mainContact" />,
                            person.isBonusContact && <FormattedMessage id="app.contacts.logins.bonusContact" />,
                        ]}
                    />
                </Stack>
            ),
        },
        {
            key: 'position',
            type: 'classification',
            head: <FormattedMessage id="app.contacts.limits.position" />,
            variant: 'muted',
            cell: person => positions.find(position => position.id === person.positionId)?.name ?? '–',
        },
        {
            key: 'userStatus',
            // "Admin", "Benutzer", "Kein Login" — short pills, not the status scale.
            type: 'status',
            width: '120px',
            head: <FormattedMessage id="app.contacts.limits.access" />,
            cell: person => {
                const status = getUserStatus(person);

                return (
                    <PaloStatusPill
                        status={status}
                        label={<FormattedMessage id={getUserStatusTranslationId(status)} />}
                    />
                );
            },
        },
        {
            key: 'permissions',
            type: 'classification',
            head: <FormattedMessage id="app.contacts.limits.orderView" />,
            cell: person =>
                // An admin sees every order of the company, whatever the switch says (O-16).
                UserStatus.ShopAdmin === getUserStatus(person) ? (
                    <FormattedMessage id="app.contacts.limits.orderView.admin" />
                ) : isLoginUser(person) ? (
                    <Box
                        component="span"
                        role="button"
                        title={intl.formatMessage({
                            id: person.permissions.includes(EmployeePermissions.EmployeeOrders)
                                ? 'app.contacts.logins.tooltip.activatedPermission'
                                : 'app.contacts.logins.tooltip.deactivatedPermission',
                        })}
                        onClick={event => {
                            // The row opens the person; this only toggles.
                            event.stopPropagation();
                            togglePermission(person);
                        }}
                        sx={{ cursor: 'pointer', '&:hover': { color: tokens.color.accent } }}
                    >
                        <FormattedMessage
                            id={
                                person.permissions.includes(EmployeePermissions.EmployeeOrders)
                                    ? 'app.contacts.limits.orderView.company'
                                    : 'app.contacts.limits.orderView.own'
                            }
                        />
                    </Box>
                ) : (
                    '–'
                ),
        },
        {
            key: 'limitOrder',
            type: 'amount',
            // Wide enough for its heading on one line.
            width: '180px',
            currency,
            head: <FormattedMessage id="app.contacts.limits.perOrder" />,
            cell: person =>
                isLoginUser(person) ? (
                    <PaloLimitCell
                        value={person.perOrderOrderCeiling}
                        hint={cappedHintOf(person.perOrderOrderCeiling, person.monthlyOrderCeiling)}
                        onSave={value => saveLimit(person, { perOrderOrderCeiling: value })}
                    />
                ) : (
                    '–'
                ),
        },
        {
            key: 'limitMonth',
            type: 'amount',
            currency,
            head: <FormattedMessage id="app.contacts.limits.monthly" />,
            cell: person =>
                isLoginUser(person) ? (
                    <PaloLimitCell
                        value={person.monthlyOrderCeiling}
                        choices={orderCeilings}
                        onSave={value => saveLimit(person, { monthlyOrderCeiling: value })}
                    />
                ) : (
                    '–'
                ),
        },
        {
            key: 'used',
            type: 'name',
            head: (
                <FormattedMessage
                    id="app.contacts.limits.used"
                    values={{ month: intl.formatDate(new Date(), { month: 'long' }) }}
                />
            ),
            cell: person =>
                isLoginUser(person) ? (
                    <PaloUsageCell used={person.currentMonthlyOrderSum} monthlyLimit={person.monthlyOrderCeiling} />
                ) : (
                    <Link
                        component="button"
                        underline="hover"
                        onClick={event => {
                            event.stopPropagation();
                            openModal(person);
                        }}
                    >
                        <FormattedMessage id="app.contacts.limits.createLogin" />
                    </Link>
                ),
        },
    ];

    const rowActions = (person: Person): PaloRowAction[] => {
        const actions: PaloRowAction[] = [];
        const isSelf = user?.contact.id === person.id;

        if (!person.isMainContact) {
            actions.push({
                label: <FormattedMessage id="app.contacts.logins.mainContact" />,
                onClick: () => dispatch(updateMainContact(person.id)),
            });
        }
        if (!isSelf) {
            actions.push({
                label: <FormattedMessage id="app.contacts.logins.delete.employee" />,
                onClick: () => {
                    setSelectedUser(person);
                    setIsDeleteEmployeeModalOpen(true);
                },
            });
        }
        if (!isSelf && isLoginUser(person)) {
            actions.push({
                label: <FormattedMessage id="app.contacts.logins.delete.user" />,
                onClick: () => {
                    setSelectedUser(person);
                    setIsDeleteUserModalOpen(true);
                },
            });
        }
        if (!isSelf && !person.isBonusContact) {
            actions.push({
                label: <FormattedMessage id="app.contacts.logins.bonusContact" />,
                onClick: () => toggleBonusContact(person),
            });
        }
        if (!isSelf && 1 !== bonusContactCount && person.isBonusContact) {
            actions.push({
                label: <FormattedMessage id="app.contacts.logins.delete.bonusContact" />,
                onClick: () => toggleBonusContact(person),
            });
        }

        return actions;
    };

    const headerActions = (
        <Stack direction="row" gap={1}>
            {accountNeedsToBeChecked && (
                <Button variant="outlined" color="primary" onClick={() => dispatch(updateAccountLastChecked())}>
                    <FormattedMessage id="app.contacts.logins.data" />
                </Button>
            )}
            <Button id="add-new-user" variant="contained" color="primary" onClick={() => openModal()}>
                <FormattedMessage id="app.common.newUser" />
            </Button>
        </Stack>
    );

    return (
        <UrlFilter>
            <PaloListView<Person, number>
                title={<FormattedMessage id="app.navigation.contactsLogins" />}
                headerActions={headerActions}
                notice={
                    <Stack direction={{ xs: 'column', md: 'row' }} gap="10px" sx={{ mb: '12px' }}>
                        {pendingApprovals > 0 && (
                            <Box sx={{ flex: 1 }}>
                                <PaloNotice tone="warn">
                                    <FormattedMessage
                                        id="app.contacts.limits.pendingApprovals"
                                        values={{
                                            count: pendingApprovals,
                                            b: (chunks: ReactNode) => <strong>{chunks}</strong>,
                                        }}
                                    />{' '}
                                    <Link component={RouterLink} to={ROUTES.orderApprovals}>
                                        <FormattedMessage id="app.contacts.limits.toApprovals" />
                                    </Link>
                                </PaloNotice>
                            </Box>
                        )}
                        <Box sx={{ flex: 1 }}>
                            <PaloNotice tone="info">
                                <FormattedMessage
                                    id="app.contacts.limits.rule"
                                    values={{ b: (chunks: ReactNode) => <strong>{chunks}</strong> }}
                                />
                            </PaloNotice>
                        </Box>
                    </Stack>
                }
                filters={<PaloContactsLoginsFilter />}
                filterChips={<PaloFilterChips names={filterNames} />}
                sort={{ key: sortKey, order: sortOrder as 'asc' | 'desc' }}
                onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
                searchValue={searchParams.get('search') ?? ''}
                searchPlaceholder={intl.formatMessage({ id: 'app.common.searchPlaceholder' })}
                onSearchChange={value => {
                    const next = new URLSearchParams(searchParams);
                    if (value) {
                        next.set('search', value);
                    } else {
                        next.delete('search');
                    }
                    next.set('page', '1');
                    setSearchParams(next, { replace: true });
                }}
                columns={columns}
                items={people}
                getRowId={person => person.id}
                requesting={requestingUser}
                emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
                onRowClick={openModal}
                rowActions={rowActions}
                pagination={{
                    count: data.total,
                    page,
                    limit,
                    onPageChange: newPage => updateParams({ page: String(newPage) }),
                    onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
                }}
            >
                <Box sx={{ marginTop: '10px', fontSize: '12px', color: tokens.color.ink3 }}>
                    <FormattedMessage id="app.contacts.limits.footer" />
                </Box>
                <ContactsLoginsDetailModal visible={showUpsertModal} user={selectedUser} onClose={closeModal} />
                <ContactsLoginsDeleteEmployeeModal
                    isOpen={isDeleteEmployeeModalOpen}
                    user={selectedUser}
                    onClose={() => setIsDeleteEmployeeModalOpen(false)}
                    onConfirm={confirmDeleteEmployee}
                />
                <ContactsLoginsDeleteLoginModal
                    isOpen={isDeleteUserModalOpen}
                    user={selectedUser}
                    onClose={() => setIsDeleteUserModalOpen(false)}
                    onConfirm={confirmDeleteUser}
                />
            </PaloListView>
        </UrlFilter>
    );
};
